// ABOUTME: Renders a dashboard link as a bare single-line row with a hover-only domain.
// ABOUTME: Carries the edit-mode grab handle, row controls, and the sponsored treatment.

import React, { useState, memo } from "react";
import type { DraggableProvidedDragHandleProps } from "@hello-pangea/dnd";
import { UrlData } from "../services/firebase";
import { domainOf, linkLabel } from "../utils/linkLabel";
import { sanitizeUrl } from "../utils/urlValidation";
import { useUIStore } from "../stores/ui";
import { trackAdClick } from "../services/adAnalytics";

/** Extracts hostname (minus "www.") or falls back to raw URL */

interface AppCardProps {
  app: UrlData;
  editing?: boolean;
  /** Spread onto the grab handle so the row drags from one deliberate target. */
  dragHandleProps?: DraggableProvidedDragHandleProps | null;
  onEdit: (app: UrlData) => void;
  onDelete: (id: string) => void;
}

const AppCard: React.FC<AppCardProps> = ({
  app,
  editing = false,
  dragHandleProps,
  onEdit,
  onDelete,
}) => {
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [faviconFailed, setFaviconFailed] = useState(false);
  const spacing = useUIStore((s) => s.spacing);
  const openLinksInNewTab = useUIStore((s) => s.openLinksInNewTab);

  // Random delay so cards don't breathe in unison
  const [breatheDelay] = useState(() => `${Math.random() * 0.5}s`);

  const domain = domainOf(app.url) || app.url;
  const label = linkLabel(app);
  // A user-picked card color replaces the page background, but every text
  // token was derived from the *page*. Re-derive them for this surface so a
  // navy card on a cream page gets light text instead of vanishing.
  // sz=64 is plenty for a control rendered at 20-32px; sz=128 was 4x oversized.
  const favicon = `https://www.google.com/s2/favicons?domain=${domain}&sz=64`;
  // Validated once here so the href is never a javascript:/data: URL.
  const href = sanitizeUrl(app.url) || undefined;

  // Spacing-dependent classes
  const rowHeight = { compact: "h-7", default: "h-8", relaxed: "h-10" }[spacing];
  const cardPadding = { compact: "px-1.5", default: "px-2", relaxed: "px-2.5" }[spacing];
  const cardGap = { compact: "gap-2", default: "gap-2.5", relaxed: "gap-3" }[spacing];
  const iconSize = { compact: "w-4 h-4", default: "w-[18px] h-[18px]", relaxed: "w-6 h-6" }[
    spacing
  ];
  const imgSize = { compact: "w-3 h-3", default: "w-3.5 h-3.5", relaxed: "w-4 h-4" }[spacing];
  const titleSize = {
    compact: "text-[0.8125rem]",
    default: "text-sm",
    relaxed: "text-[0.9375rem]",
  }[spacing];

  /**
   * The card is a real <a href>, so the browser owns navigation: Enter, middle
   * click, Cmd/Ctrl-click, right-click → copy address, and the hover status bar
   * all work for free. This handler only suppresses navigation in the cases
   * where a click shouldn't follow the link.
   */
  const handleCardClick = (e: React.MouseEvent) => {
    if (confirmingDelete || editing) {
      e.preventDefault();
      return;
    }
    // Clicks on the edit/delete controls are theirs, not the link's.
    if ((e.target as HTMLElement).closest(".appcard-action")) {
      e.preventDefault();
      return;
    }
    if (!href) {
      e.preventDefault();
      alert("This link appears to be invalid or potentially unsafe.");
      return;
    }
    if (app.isAd) trackAdClick({ id: app.id, title: app.name || "", url: app.url, sponsor: "" });
  };

  // Links are bare rows; a sponsored card is quarantined behind a dashed
  // outline so it can never be mistaken for one of the user's own links.
  const rootClasses = app.isAd
    ? `relative group flex items-center ${cardPadding} py-1.5 ${cardGap} rounded-[4px]
       border border-dashed border-[var(--color-border)]
       focus-within:ring-2 focus-within:ring-[var(--color-accent)]
       text-[var(--color-text-tertiary)]`
    : `relative group flex items-center ${rowHeight} ${cardPadding} ${cardGap} rounded-[4px]
       transition-colors focus-within:ring-2 focus-within:ring-[var(--color-accent)]
       hover:bg-[var(--color-bg-hover)]
       ${editing && !confirmingDelete ? "animate-[breathe_1.5s_ease-in-out_infinite]" : ""}`;

  return (
    <div
      className={rootClasses}
      style={editing && !confirmingDelete ? { animationDelay: breatheDelay } : undefined}
      data-sponsored={app.isAd ? "true" : undefined}
    >
      {/* Sponsored label: mono caps in token text, no fill of its own. */}
      {app.isAd && (
        <span
          className="absolute top-1 right-1.5 text-xs uppercase tracking-[0.12em]
                     font-mono text-[var(--color-text-secondary)]"
        >
          Sponsored
        </span>
      )}

      {/* A chosen colour marks the row from its leading edge. Fill is the
          hover channel, so painting the row would make a marked row read as
          a selected one. */}
      {app.bgColor && !app.isAd && (
        <span
          data-testid="color-marker"
          aria-hidden="true"
          className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-[55%] rounded-full"
          style={{ backgroundColor: app.bgColor }}
        />
      )}

      {/* Edit mode: the row's grab target. Borderless rows have no edge to
          grab, so the handle is the one place a drag starts from. */}
      {editing && !app.isAd && dragHandleProps && (
        <span
          data-testid="grab-handle"
          {...dragHandleProps}
          aria-label={`Reorder ${label}`}
          className={`appcard-action flex-shrink-0 flex items-center justify-center ${rowHeight} w-3
                      cursor-grab active:cursor-grabbing text-[var(--color-text-tertiary)]
                      hover:text-[var(--color-text-secondary)] focus:outline-none
                      focus-visible:text-[var(--color-accent)]`}
        >
          <svg width="8" height="14" viewBox="0 0 8 14" fill="currentColor" aria-hidden="true">
            <circle cx="2" cy="3" r="1" />
            <circle cx="6" cy="3" r="1" />
            <circle cx="2" cy="7" r="1" />
            <circle cx="6" cy="7" r="1" />
            <circle cx="2" cy="11" r="1" />
            <circle cx="6" cy="11" r="1" />
          </svg>
        </span>
      )}

      {/* The link surface: a real anchor, so the browser owns navigation. */}
      <a
        href={href}
        // Ads always open out and never pass link equity. User links follow the
        // "Open links in a new tab" setting (on by default); either way the
        // anchor means Cmd/middle-click still gives the user the other choice.
        {...(app.isAd
          ? { target: "_blank" as const, rel: "noopener noreferrer nofollow" }
          : openLinksInNewTab
            ? { target: "_blank" as const, rel: "noopener noreferrer" }
            : {})}
        onClick={handleCardClick}
        title={app.isAd ? "Sponsored link" : undefined}
        className={`flex items-center ${cardGap} min-w-0 flex-1 no-underline
                    text-inherit focus:outline-none`}
      >
        {/* Favicon/avatar */}
        <div
          className={`${iconSize} flex-shrink-0
                   flex items-center justify-center relative overflow-hidden`}
        >
          {faviconFailed ? (
            // Letter avatar rather than a broken-image glyph, which is what
            // blocked or missing Google favicons used to render as.
            <span
              className={`${imgSize} flex items-center justify-center font-semibold
                        text-[var(--color-text-tertiary)] uppercase select-none`}
              aria-hidden="true"
            >
              {(label || domain || "?").trim().charAt(0)}
            </span>
          ) : (
            <img
              src={favicon}
              alt=""
              aria-hidden="true"
              width={64}
              height={64}
              loading="lazy"
              decoding="async"
              className={`${imgSize} object-contain`}
              style={{ filter: "none" }}
              onError={() => setFaviconFailed(true)}
            />
          )}
        </div>

        {/* Title, then the domain as a trailing detail */}
        {app.isAd ? (
          <div className="flex flex-col min-w-0 flex-1 pr-16">
            <span
              className={`truncate font-medium ${titleSize} text-[var(--color-text-secondary)] leading-tight`}
            >
              {label}
            </span>
            <span className="truncate text-xs text-[var(--color-text-tertiary)]">
              {/* Labeled in the text flow as well as the corner badge, so a
                narrow card or a screen reader still gets the disclosure. */}
              {`Sponsored · ${app.description || domain}`}
            </span>
          </div>
        ) : (
          <>
            <span
              className={`truncate min-w-0 flex-1 font-medium ${titleSize} text-[var(--color-text-primary)] leading-none`}
            >
              {label}
            </span>
            {/* The domain is reference detail, not the row's subject: it only
              surfaces when the pointer is on the row. */}
            <span
              className={`shrink-0 max-w-0 group-hover:max-w-[45%] group-focus-within:max-w-[45%]
                        overflow-hidden truncate text-right text-xs
                        font-mono text-[var(--color-text-secondary)]
                        opacity-0 group-hover:opacity-100 group-focus-within:opacity-100
                        transition-all`}
            >
              {domain}
            </span>
          </>
        )}
      </a>

      {/* Edit mode: row controls. Siblings of the anchor, not children —
          interactive content may not nest inside <a>. */}
      {editing && !app.isAd && !confirmingDelete && (
        <div className="flex items-center gap-0.5 [@media(pointer:coarse)]:gap-6 flex-shrink-0">
          <button
            type="button"
            className="tap-target appcard-action p-1 rounded
                       hover:bg-[var(--color-bg-hover)]
                       transition-colors text-[var(--color-text-tertiary)]
                       hover:text-[var(--color-accent)]"
            aria-label={`Edit ${label}`}
            onClick={() => onEdit(app)}
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
              <path d="m15 5 4 4" />
            </svg>
          </button>
          <button
            type="button"
            className="tap-target appcard-action p-1 rounded
                       hover:bg-[var(--color-bg-hover)]
                       transition-colors text-[var(--color-text-tertiary)]
                       hover:text-[var(--color-danger)]"
            aria-label={`Delete ${label}`}
            onClick={() => setConfirmingDelete(true)}
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      )}

      {/* Delete confirmation overlay */}
      {confirmingDelete && (
        <div
          className="absolute inset-0 z-10 flex items-center justify-between gap-2 px-2
                     rounded-[4px] bg-[var(--color-bg-secondary)]
                     border border-[color-mix(in_srgb,var(--color-danger)_40%,transparent)]
                     animate-[fadeIn_150ms_ease-out]"
        >
          <span className="truncate text-xs font-medium text-[var(--color-danger)]">
            Delete this link?
          </span>
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              type="button"
              className="tap-target px-2 py-0.5 text-xs font-medium rounded
                         border border-[var(--color-border)] bg-[var(--color-bg)]
                         text-[var(--color-text-primary)]
                         hover:bg-[var(--color-bg-hover)] transition-colors"
              onClick={() => setConfirmingDelete(false)}
            >
              Keep
            </button>
            <button
              type="button"
              className="tap-target px-2 py-0.5 text-xs font-medium rounded
                         bg-[var(--color-danger)] text-[var(--color-text-inverse)]
                         hover:opacity-90 transition-colors"
              onClick={() => {
                setConfirmingDelete(false);
                onDelete(app.id);
              }}
            >
              Delete
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(AppCard);
